import {
  ChangeDetectorRef,
  // eslint-disable-next-line @angular-eslint/component-selector, jsdoc/no-types, import/no-deprecated,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MatCardAppearance } from '@angular/material/card';
import { MatDialogRef } from '@angular/material/dialog';
import { zip } from 'rxjs';
import { CalendarEvent } from 'src/app/models/calendarEvent';
import { ClientEntity } from 'src/app/models/entities/client.entity';
import {
  RoleRateItem,
  BillableItemEntity,
} from 'src/app/models/entities/project.entity';
import { TimeSheetEntity } from 'src/app/models/entities/timesheet.entity';
import { UserEntity } from 'src/app/models/entities/user.entity';
import { BillableItemService } from 'src/app/services/api/billableItems.service';
import { ClientService } from 'src/app/services/api/clients.service';
import { TimeSheetService } from 'src/app/services/api/timesheet.service';
import { UserService } from 'src/app/services/api/user.service';
import { CalendarService } from 'src/app/services/calendar.service';
import { DateHelperService } from 'src/app/services/dateHelper.service';
import { Utils } from 'src/app/utils';
import { ActivatedRoute, Router } from '@angular/router';
import { UserSettingsService } from 'src/app/services/api/usersetting.service';
import { MatSnackBar } from '@angular/material/snack-bar';

import { Component, OnInit } from '@angular/core';

@Component({
  selector: 'app-timesheet-edit',
  templateUrl: './timesheet.component.html',
  styleUrls: ['./timesheet.component.scss'],
  standalone: false,
})
export class TimesheetEditComponent implements OnChanges {
  @Input() public billableItem: BillableItemEntity | undefined;

  @Input() public notes: string | undefined;

  @Input() public card: string | undefined;

  @Input() public cardClientKey: string | null = null;

  @Input() public boardSelectedItems: string[] = [];
  
  @Input() public boardDefaultProject: string | null = null;

  @Input() public item: TimeSheetEntity | undefined;

  @Input() public popup: boolean = false;

  @Input() public calendarIdsForDay: string[] | undefined;

  @Input() public selectedDate: number | undefined;

  @Input() public timeSheet: TimeSheetEntity | undefined;

  public calendar: CalendarEvent[] | undefined;
  public calendarLoading: boolean = true;
  public calendarLoaded: boolean = false;
  public hasGoogleAccess = true;
  public hasMicrosoftAccess = true;
  public showSettings = false;
  public selectedCalenderId: string | undefined;
  public showCalendar: boolean = true;
  public multipleSelected: boolean = false;
  public selectedItems: string[] = [];
  public groupedCalendarEvents: { [key: string]: CalendarEvent[] } = {};
  private isLoadingFromCalendar: boolean = false;

  @Output() public actioned: EventEmitter<any> = new EventEmitter<any>();

  @Output() public timerStarted: EventEmitter<TimeSheetEntity> =
    new EventEmitter<TimeSheetEntity>();

  @Output() public timerStopped: EventEmitter<TimeSheetEntity> =
    new EventEmitter<TimeSheetEntity>();

  @Output() public scrollRequested: EventEmitter<void> =
    new EventEmitter<void>();

  @Output() public billableItemChanged: EventEmitter<BillableItemEntity> =
    new EventEmitter<BillableItemEntity>();

  @Output() public calendarEventSelected: EventEmitter<boolean> =
    new EventEmitter<boolean>();

  public timesheets: TimeSheetEntity[] | undefined;
  public isProjectsLoading: boolean = false;
  public isItemsLoading: boolean = false;

  // Getter to return timesheets without running timers for display in the chip list
  get filteredTimesheets(): TimeSheetEntity[] {
    if (!this.timesheets) return [];
    return this.timesheets.filter(item => item.to); // Only include items that have an end time
  }
  
  // Getter to return only running timers
  get runningTimers(): TimeSheetEntity[] {
    if (!this.timesheets) return [];
    return this.timesheets.filter(item => !item.to); // Only include items that don't have an end time
  }

  // Getter to check if the current form is for a running timer
  get isRunningTimer(): boolean {
    return this.timeSheetForm.value.key && 
           !this.timeSheetForm.value.to;
  }

  public isEdit: boolean = false;
  public hideAdd: boolean = false;

  public timeSheetForm: FormGroup = this.formBuilder.group({
    billableItem: ['', Validators.required],
    card: [''],
    boardKey: [''],
    item: ['', Validators.required],
    notes: [''],
    from: ['', Validators.required],
    to: [''],
    total: [''],
    key: [''],
    calendarId: [''],
    date: [new Date()],
  });
  public billableItems: BillableItemEntity[] = [];
  public clients: ClientEntity[] = [];
  public filteredClients: ClientEntity[] = [];
  public items: RoleRateItem[] | undefined = [];
  public timeDifference: string | undefined;
  public startButtonDelay: boolean = false;

  private readonly GAP_SIZE = 30; // Gap between panel and modal in pixels
  public panelWidth: string = '300px';
  private readonly MODAL_WIDTH = 735;
  private readonly PADDING = 20;
  private readonly MARGIN = 55;

  public isCalendarExpanded: boolean = window.innerWidth >= 1024;

  private allBillableItems: BillableItemEntity[] = [];

  // Add a flag to prevent multiple timer start operations simultaneously  
  private isStartingTimer: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    private clientService: ClientService,
    private userService: UserService,
    private timeSheetService: TimeSheetService,
    private calendarService: CalendarService,
    private cdr: ChangeDetectorRef,
    public dialogRef: MatDialogRef<any>,
    private dateHelper: DateHelperService,
    private billableItemsService: BillableItemService,
    private elementRef: ElementRef,
    private route: ActivatedRoute,
    private router: Router,
    private userSettingsService: UserSettingsService,
    private snackBar: MatSnackBar
  ) {
    this.calculatePanelWidth();
    window.addEventListener('resize', () => {
      this.calculatePanelWidth();
      this.isCalendarExpanded = window.innerWidth >= 1024;
    });
  }

  ngOnDestroy() {
    window.removeEventListener('resize', () => this.calculatePanelWidth());
  }

  /**
   * Utility method to ensure notes are properly set with the full card name
   * @param notesValue The notes value to set
   */
  private ensureNotesSet(notesValue: string | undefined): void {
    if (!notesValue) return;
    
    console.log('Ensuring notes are set with full value:', notesValue);
    
    // Only set if form exists
    if (this.timeSheetForm && this.timeSheetForm.controls['notes']) {
      const currentNotes = this.timeSheetForm.controls['notes'].value;
      
      // Only update if empty or matches previous card name
      if (!currentNotes) {
        console.log('Setting empty notes field to:', notesValue);
        this.timeSheetForm.controls['notes'].setValue(notesValue);
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    
    
    if (changes['billableItem'] && changes['billableItem'].currentValue) {
      
      this.billableItem = changes['billableItem'].currentValue;
      this.items = this.billableItem?.items;
      
      // Only populate dropdowns if the billableItem actually changed
      if (changes['billableItem'].previousValue?.key !== changes['billableItem'].currentValue?.key) {
        this.populateDropDowns();
      }
    }

    // Add specific handling for the notes input when it changes
    if (changes['notes'] && changes['notes'].currentValue) {
      const newNotes = changes['notes'].currentValue;
      // Make sure we're getting the full string and not just the first character
      console.log('Received notes change:', newNotes);
      this.ensureNotesSet(newNotes);
    }

    if (changes['item'] && changes['item'].currentValue) {
      
      const item = changes['item'].currentValue;
      
      // Only process the item if we already have billable items loaded
      if (this.billableItems && this.billableItems.length > 0) {
        this.processExistingItem(item);
      }
      // Otherwise, the item will be processed once billable items are loaded in ngOnInit
    }
    
    // If board selected items or default project changes, update the dropdown
    if ((changes['boardSelectedItems'] || changes['boardDefaultProject']) && 
        this.billableItems && this.billableItems.length > 0) {
      this.populateDropDowns();
    }
  }

  ngOnInit() {
    this.userService.currentUser$.subscribe((user) => {
      this.clientService.get().subscribe((clients: any) => {
        this.clients = clients;
        this.filteredClients = clients; // Initialize filteredClients with all clients initially
        
        // Set loading state to true before fetching billable items
        this.isProjectsLoading = true;
        
        this.billableItemsService.getBillableItems().subscribe((items) => {
          
          // Store all billable items first
          this.allBillableItems = items;
          
          // If we have board selected items, filter the items
          if (this.boardSelectedItems && this.boardSelectedItems.length > 0) {
            this.billableItems = items.filter(item => this.boardSelectedItems.includes(item.key));
          } else {
          this.billableItems = items;
          }
          
          // Filter clients based on billable items
          this.updateFilteredClients();
          
          // Now that we have billable items, decide how to initialize the form
          if (this.item) {
            // Editing an existing item, so process it
            this.processExistingItem(this.item);
          } else if (this.popup) {
            // Creating a NEW item in POPUP mode, use localStorage
            this.loadLastPopupSelection();
          } else {
            // Creating a NEW item in EMBEDDED mode, use existing logic
            this.autoSelectProject();
          }

          // Set the selected date if provided and not already set by an existing item
          if (!this.item && this.selectedDate) {
            this.timeSheetForm.controls['date'].setValue(new Date(this.selectedDate));
          }
          
          this.populateDropDowns();
          
          // Loading complete
          this.isProjectsLoading = false;
        },
        error => {
          console.error('Error loading billable items:', error);
          this.isProjectsLoading = false;
        });

        this.loadExisitingTimeSheets();

        this.timeSheetForm.controls['date'].valueChanges.subscribe(
          (date: any) => {
            if (
              date &&
              (this.hasGoogleAccess || this.hasMicrosoftAccess) &&
              !this.isLoadingFromCalendar
            ) {
              this.loadCalendars();
            }
          }
        );

        this.timeSheetForm.controls['from'].valueChanges.subscribe(
          (from: any) => {
            if (from && this.timeSheetForm.controls['to'].value) {
              this.timeSheetForm.controls['total'].setValue(
                this.dateHelper.calculateTimeDifferenceMinutes(
                  from,
                  this.timeSheetForm.controls['to'].value
                )
              );
              this.timeDifference = this.calculateTimeDifference(
                from,
                this.timeSheetForm.controls['to'].value
              );
            }
          }
        );

        this.timeSheetForm.controls['to'].valueChanges.subscribe((to: any) => {
          if (to && this.timeSheetForm.controls['from'].value) {
            this.timeSheetForm.controls['total'].setValue(
              this.dateHelper.calculateTimeDifferenceMinutes(
                this.timeSheetForm.controls['from'].value,
                to
              )
            );
            this.timeDifference = this.calculateTimeDifference(
              this.timeSheetForm.controls['from'].value,
              to
            );
          }
        });

        this.calendarService.hasGoogleAccess().subscribe({
          next: (hasAccess) => {
            this.hasGoogleAccess = hasAccess;
            if (hasAccess) {
              this.showCalendar = true;
              this.loadCalendars();
            }
          },
          error: (error) => {
            console.error('Error checking Google calendar access:', error);
            this.hasGoogleAccess = false;
          }
        });
        this.calendarService.hasMicrosoftAccess().subscribe({
          next: (hasAccess) => {
            this.hasMicrosoftAccess = hasAccess;
            if (hasAccess) {
              this.showCalendar = true;
              this.loadCalendars();
            }
          },
          error: (error) => {
            console.error('Error checking Microsoft calendar access:', error);
            this.hasMicrosoftAccess = false;
          }
        });

        // Make sure the notes are set after initialization
        if (this.notes) {
          setTimeout(() => {
            this.ensureNotesSet(this.notes);
          }, 500);
        }
      });
    });
    // Handle Microsoft calendar redirect
    this.route.queryParams.subscribe((params) => {
      const code = params['code'];
      const state = params['state'];

      if (code && state === 'microsoft') {
        this.calendarService.handleMicrosoftRedirect(code).catch((error) => {
          console.error('Error handling Microsoft redirect:', error);
          // Handle error appropriately
        });
      }
    });
  }

  private loadCalendars() {
    this.calendarLoading = true;
    this.calendarLoaded = false;
    this.cdr.detectChanges();

    const selectedDate = this.timeSheetForm.get('date')?.value;
    if (selectedDate) {
      const startDate = new Date(selectedDate);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(selectedDate);
      endDate.setHours(23, 59, 59, 999);

      this.calendarService.getCalendarEvents(startDate, endDate).subscribe({
        next: (events) => {
          
          if (!events || events.length === 0) {
            
            this.calendar = [];
            this.groupedCalendarEvents = {};
          } else {
            
            this.calendar = events;
            this.groupCalendarEvents(events);
          }
          this.calendarLoading = false;
          this.calendarLoaded = true;
          
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error loading calendar events:', error);
          this.calendar = [];
          this.groupedCalendarEvents = {};
          this.calendarLoading = false;
          this.calendarLoaded = false; // Fixed: should be false on error
          this.cdr.detectChanges();
        },
      });
    }
  }

  private groupCalendarEvents(events: CalendarEvent[]) {
    
    if (!events?.length) {
      
      this.groupedCalendarEvents = {};
      return;
    }

    this.groupedCalendarEvents = events.reduce(
      (groups: { [key: string]: CalendarEvent[] }, event) => {
        if (!event) {
          
          return groups;
        }

        const source = event.source || 'unknown';
        
        if (!groups[source]) {
          groups[source] = [];
        }
        groups[source].push(event);
        return groups;
      },
      {}
    );

    
    this.cdr.detectChanges();
  }

  public getClientItems(client: ClientEntity): BillableItemEntity[] {
    if (client) {
      // Filter billableItems to only those belonging to this client
      var items = this.billableItems.filter((x) => x.client === client.key);

      // Sort the items alphabetically by name
      if (items) {
        items.sort((a, b) => a.name.localeCompare(b.name));
      }
      
      // If there's only one item and no item is selected yet, auto-select it
      if (items.length === 1 && !this.timeSheetForm.controls['billableItem'].value) {
        setTimeout(() => {
          this.timeSheetForm.controls['billableItem'].setValue(items[0].key);
          // Pass false for emitChange to prevent circular dependency
          this.billableItemSelected({ value: items[0].key }, true, false);
        }, 0);
      }
      
      return items;
    } else {
      return [];
    }
  }

  private filterItemsByUserRoles(
    items: RoleRateItem[],
    user: UserEntity
  ): RoleRateItem[] {
    
    
    
    if (!items || !user?.roles) {
      
      return [];
    }

    const filtered = items.filter((item) => {
      // If item has no roles defined, show to everyone
      if (!item.roles || item.roles.length === 0) {
        return true;
      }

      // Check if user has any of the required roles
      return item.roles.some((role) => user.roles.includes(role));
    });
    
    
    return filtered;
  }

  private populateDropDowns() {
    if (this.card && this.timeSheetForm && this.cardClientKey) {
      this.timeSheetForm.controls['card'].setValue(this.card);
    }

    // Ensure we're setting the full notes string if available
    if (this.notes) {
      // Use our utility method for consistent handling
      this.ensureNotesSet(this.notes);
    }
    
    // Set the billableItem if it was automatically selected
    // Only emit the change event if we're actually changing the value
    if (this.billableItem && this.timeSheetForm.controls['billableItem'].value !== this.billableItem.key) {
      this.timeSheetForm.controls['billableItem'].setValue(this.billableItem.key);
      // Don't emit the event here as it can cause a circular dependency
      // The parent component already knows about this billableItem
    }
    
    // Filter billable items based on board selected items if available
    if (this.boardSelectedItems && this.boardSelectedItems.length > 0 && this.allBillableItems) {
      // Filter billable items to only those selected in the board
      this.billableItems = this.allBillableItems.filter(item => this.boardSelectedItems.includes(item.key));
      
      // Set default project if available and included in filtered items
      if (this.boardDefaultProject && !this.timeSheetForm.controls['billableItem'].value) {
        const defaultItem = this.billableItems.find(item => item.key === this.boardDefaultProject);
        if (defaultItem) {
          this.timeSheetForm.controls['billableItem'].setValue(defaultItem.key);
          // Don't call billableItemSelected here, just set up the items
          this.items = this.filterItemsByUserRoles(defaultItem.items, this.userService.currentUser);
          if (this.items) {
            this.items.sort((a, b) => a.name.localeCompare(b.name));
          }
      }
      }
    } else if (this.allBillableItems) {
      // If no board selected items, use all billable items
      this.billableItems = [...this.allBillableItems];
    }
  }
  private loadExisitingTimeSheets() {
    if (this.card) {
      this.timeSheetForm.controls['card'].setValue(this.card);

      this.timeSheetService
        .getItemsForCard(this.card, this.userService.currentUser.key)
        .subscribe((items: any) => {
          this.timesheets = items;
          
          // Order by date
          if (this.timesheets) {
            this.timesheets.sort((a, b) => {
              return b.date - a.date;
            });
            
            // Check for a running timer (no end time)
            const runningTimer = this.timesheets.find(t => !t.to);
            if (runningTimer) {
              // Auto-select the running timer
              this.itemSelected(runningTimer);
            } else {
              // Only set most frequent item if no running timer exists
              var mostItem = this.findMostFrequentItem(items);
              this.timeSheetForm.controls['item'].setValue(mostItem);
            }
          }
        });
    }
  }

  public onNoClick(): void {
    this.dialogRef.close();
  }
  public checkBoxChanged(event: Event, calendarItem: CalendarEvent) {
    const checkbox = event.target as HTMLInputElement;
    calendarItem.checked = checkbox.checked;

    // Update selected items
    if (checkbox.checked) {
      this.selectedItems.push(calendarItem.id);
    } else {
      const index = this.selectedItems.indexOf(calendarItem.id);
      if (index > -1) {
        this.selectedItems.splice(index, 1);
      }
    }

    this.multipleSelected = this.selectedItems.length > 0;
    this.cdr.detectChanges();
  }
  // Helper method to save last selected item to user settings
  private saveLastSelectedItem(itemKey: string): void {
    const user = this.userService.currentUser;
    if (user && user.key) {
      this.userSettingsService.update(user.key, 'lastSelectedItem', itemKey)
        .catch(error => console.error('Error saving last selected item:', error));
    }
  }

  public billableItemSelected(
    event: { value: string } | null | undefined,
    setBillable: boolean = true,
    emitChange: boolean = true
  ): BillableItemEntity | undefined {
    // Early return if event or value is invalid
    if (!event?.value) {
      this.resetSelection();
      return undefined;
    }

    // Find the selected billable item
    const selectedItem = this.billableItems.find(
      (item) => item.key === event.value
    );

    if (!selectedItem) {
      this.resetSelection();
      return undefined;
    }

    // Update component state
    this.isItemsLoading = true;
    
    // Make sure we have a form before proceeding
    if (this.timeSheetForm) {
      // Filter items by user roles
    this.items = this.filterItemsByUserRoles(
      selectedItem.items,
      this.userService.currentUser
    );

    // Sort the items alphabetically by name
    if (this.items) {
      this.items.sort((a, b) => a.name.localeCompare(b.name));
    }
      
      // Auto-select the first item if available and if the form control exists
      if (this.items && this.items.length > 0 && 
          this.timeSheetForm.controls['item'] && 
          !this.timeSheetForm.controls['item'].value) {
        console.log('Auto-selecting first item:', this.items[0].name);
        this.timeSheetForm.controls['item'].setValue(this.items[0].name);
      }
    } else {
      console.error('TimeSheetForm not initialized in billableItemSelected');
    }
    
    this.isItemsLoading = false;

    if (setBillable) {
      this.billableItem = selectedItem;
    }

    // Save the selected item to user settings
    this.saveLastSelectedItem(selectedItem.key);

    // Only emit the selection change event if requested
    // This prevents circular dependencies when called programmatically
    if (emitChange) {
      this.billableItemChanged.emit(selectedItem);
    }

    return selectedItem;
  }
  public projectSelectedByProject(item: BillableItemEntity) {
    
    if (item) {
      this.items = this.filterItemsByUserRoles(item.items, this.userService.currentUser);
      
    }
  }

  public formatMinutes(timeSheet: TimeSheetEntity): string {
    if (timeSheet.to) {
      const diffInMilliseconds = timeSheet.total * 60 * 1000;
      const hours = Math.floor(diffInMilliseconds / 1000 / 60 / 60);
      const minutes = Math.floor(diffInMilliseconds / 1000 / 60) % 60;

      return `${hours} hours ${minutes} minutes`;
    } else {
      //diffInMilliseconds timesheet from  to date now
      const diffInMilliseconds = Math.abs(
        new Date().getTime() - timeSheet.date
      );
      const hours = Math.floor(diffInMilliseconds / 1000 / 60 / 60);
      const minutes = Math.floor(diffInMilliseconds / 1000 / 60) % 60;
      return `${hours} hours ${minutes} minutes`;
    }
  }

  public stopItemTimer(item: TimeSheetEntity) {
    console.log('### DEBUG: stopItemTimer called for item:', item.key);
    
    // Set the current time as the end time
    const now = new Date();
    const currentTime = now.toLocaleTimeString('en-ZA', {
      hour12: false,
      hour: 'numeric',
      minute: 'numeric',
    });
    
    console.log(`### DEBUG: Setting end time to ${currentTime} for timer:`, item.key);
    item.to = currentTime;
    item.total = this.dateHelper.calculateTimeDifferenceMinutes(
      item.from,
      item.to
    );
    
    // Update the form if this is the currently selected item
    if (this.timeSheetForm.controls['key'].value === item.key) {
      console.log('### DEBUG: This is the currently selected timer, updating form');
      this.timeSheetForm.patchValue({
        to: currentTime,
        total: item.total
      });
      this.timeDifference = this.calculateTimeDifference(item.from, currentTime);
      
      // Auto-save the form if this is the currently selected item
      console.log('### DEBUG: Auto-saving the form for the selected timer');
      this.save(true, true).then(() => {
        console.log('### DEBUG: Auto-save completed successfully after stopping timer');
      }).catch(error => {
        console.error('### DEBUG: Error saving timesheet after stopping timer:', error);
      });
    } else {
      console.log('### DEBUG: This is NOT the currently selected timer, updating directly');
      // Update the item in the timesheets array to reflect the change in UI
      if (this.timesheets) {
        const index = this.timesheets.findIndex(t => t.key === item.key);
        if (index !== -1) {
          console.log('### DEBUG: Updating timer in local timesheets array at index:', index);
          this.timesheets[index] = {...this.timesheets[index], to: currentTime, total: item.total};
        } else {
          console.warn('### DEBUG: Could not find timer in local timesheets array with key:', item.key);
        }
      }
      
      // Save the updated item to the database
      this.userService.currentUser$.subscribe((user) => {
        if (user) {
          console.log('### DEBUG: Calling timeSheetService.update directly for timer:', item.key);
          this.timeSheetService
            .update(item, user.key, item.key ? item.key : '')
            .then((g: any) => {
              console.log('### DEBUG: Direct update completed successfully');
              this.actioned.emit(g);
              // Force change detection to update the UI
              this.cdr.detectChanges();
            })
            .catch(error => {
              console.error('### DEBUG: Error directly stopping timer:', error);
            });
        }
      });
    }
  }

  public calculateTimeDifference(from: string, to: string): string {
    let fromTime = new Date(`2000-01-01T${from}`);
    let toTime = new Date(`2000-01-01T${to}`);

    // Check if from and to times are exactly the same
    if (from === to) {
      return '0:00'; // Return zero hours and zero minutes if times are the same
    }

    // Check if the end time is past midnight (the next day)
    if (toTime <= fromTime) {
      // Set the 'to' time to the next day
      toTime.setDate(toTime.getDate() + 1);
    }

    const diffInMilliseconds = toTime.getTime() - fromTime.getTime();
    const hours = Math.floor(diffInMilliseconds / 1000 / 60 / 60);
    const minutes = Math.floor((diffInMilliseconds / 1000 / 60) % 60);

    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  }
  public calculateTimeDifferenceDates(start: string, end: string): string {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffInMilliseconds = endDate.getTime() - startDate.getTime();
    const hours = Math.floor(diffInMilliseconds / (1000 * 60 * 60));
    const minutes = Math.floor(
      (diffInMilliseconds % (1000 * 60 * 60)) / (1000 * 60)
    );
    return `${hours}h ${minutes}m`;
  }
  public calculateTimeDifferenceDatesNumber(
    fromTime: Date,
    toTime: Date
  ): number {
    if (fromTime.getTime() === toTime.getTime()) {
      return 0; // Return zero minutes if times are the same
    }

    const diffInMilliseconds = Math.abs(toTime.getTime() - fromTime.getTime());
    const hours = Math.floor(diffInMilliseconds / 1000 / 60 / 60);

    const minutes = Math.floor(diffInMilliseconds / 1000 / 60);

    return minutes;
  }

  public loadFromCalendar(event: CalendarEvent) {
    if (!event) return;

    this.isLoadingFromCalendar = true;
    this.selectedCalenderId = event.id;

    const startDate = new Date(event.start.dateTime);
    const endDate = new Date(event.end.dateTime);

    // Format times as HH:mm for the dateHelper service
    const startTime = startDate.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
    });
    const endTime = endDate.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
    });

    const source = (event as any).source || 'unknown';
    const organizer = event.organizer || 'unknown@unknown.com';

    this.timeSheetForm.patchValue({
      date: startDate,
      from: startTime,
      to: endTime,
      notes: `${event.title || ''} (from ${source} calendar - ${organizer})`,
      total: this.dateHelper.calculateTimeDifferenceMinutes(startTime, endTime),
    });

    this.timeDifference = this.calculateTimeDifference(startTime, endTime);
    this.isLoadingFromCalendar = false;
    this.calendarEventSelected.emit(true);
  }

  public cancel() {
    throw new Error('Method not implemented.');
  }
  public resetSelection(): void {
    this.items = undefined;
    this.billableItem = undefined;
    this.billableItemChanged.emit(undefined);
  }

  private findMostFrequentItem(jsonArray: TimeSheetEntity[]) {
    // Count the occurrences of each item
    const itemCounts: { [key: string]: number } = {};
    jsonArray.forEach((obj) => {
      const item = obj.item;
      if (itemCounts[item]) {
        itemCounts[item]++;
      } else {
        itemCounts[item] = 1;
      }
    });

    // Find the item with the highest count
    let mostFrequentItem: string | null = null;
    let maxCount = 0;
    for (const item in itemCounts) {
      if (itemCounts[item] > maxCount) {
        mostFrequentItem = item;
        maxCount = itemCounts[item];
      }
    }

    return mostFrequentItem;
  }

  public saveMultiple() {
    if (!this.calendar) return;

    const selectedItems = this.calendar.filter((x) => x.checked);
    this.multipleSelected = selectedItems.length > 1;
    if (this.multipleSelected) {
      selectedItems.forEach((item) => {
        this.loadFromCalendar(item);
        this.save();
      });
    }
  }

  private saveLastPopupSelection(): void {
    if (this.popup) {
      const selection = {
        billableItem: this.timeSheetForm.value.billableItem,
        item: this.timeSheetForm.value.item,
      };
      localStorage.setItem('lastPopupTimesheetSelection', JSON.stringify(selection));
    }
  }

  private loadLastPopupSelection(): void {
    const savedSelection = localStorage.getItem('lastPopupTimesheetSelection');
    if (savedSelection) {
      const { billableItem, item } = JSON.parse(savedSelection);
      if (billableItem) {
        this.timeSheetForm.controls['billableItem'].setValue(billableItem);
        // Pass false for emitChange to prevent circular dependency during initialization
        this.billableItemSelected({ value: billableItem }, true, false);
        if (item) {
          this.timeSheetForm.controls['item'].setValue(item);
        }
      }
    } else {
      // Fallback to default project selection if nothing is in localStorage
      this.autoSelectProject();
    }
  }

  public save(emit: boolean = true, isTimerOperation: boolean = false): Promise<TimeSheetEntity> {
    console.log('### DEBUG: save() called with params:', { emit, isTimerOperation });
    console.log('### DEBUG: Current form state in save():', {
      key: this.timeSheetForm.controls['key'].value,
      from: this.timeSheetForm.controls['from'].value,
      to: this.timeSheetForm.controls['to'].value,
      isEdit: this.isEdit
    });

    // If in popup mode, cache the current selection on save
    if (this.popup) {
      this.saveLastPopupSelection();
    }

    return new Promise((resolve, reject) => {
      // Convert date to milliseconds timestamp (number)
      var date = new Date(this.timeSheetForm.controls['date'].value).getTime();
      var formValue = this.timeSheetForm.value;

      // Check if this is a running timer (has start time but no end time)
      const isRunningTimer = this.isRunningTimer;
      console.log('### DEBUG: isRunningTimer =', isRunningTimer);

      // Remove the key from form value if it's not an edit
      if (!this.isEdit) {
        console.log('### DEBUG: Not in edit mode, removing key from payload');
        const { key, ...rest } = formValue;
        formValue = rest;
      } else {
        console.log('### DEBUG: In edit mode, keeping key in payload:', this.timeSheetForm.controls['key'].value);
      }

      var payload = { ...formValue, date: date };
      console.log('### DEBUG: Final payload:', payload);

      if (this.isEdit) {
        // If updating a running timer, log it for debugging
        if (isRunningTimer) {
          console.log('### DEBUG: Updating running timer with key:', this.timeSheetForm.controls['key'].value);
        } else {
          console.log('### DEBUG: Updating completed timer with key:', this.timeSheetForm.controls['key'].value);
        }
        
        const timerKey = this.timeSheetForm.controls['key'].value;
        console.log('### DEBUG: Calling timeSheetService.update with key:', timerKey);
        
        this.timeSheetService
          .update(
            payload,
            this.userService.currentUser.key,
            timerKey ? timerKey : ''
          )
          .then(() => {
            console.log('### DEBUG: timeSheetService.update completed successfully');
            // For update, we resolve with the payload since update returns void
            const updatedItem = {
              ...payload,
              key: this.timeSheetForm.controls['key'].value,
              isTimerOperation: isTimerOperation
            } as TimeSheetEntity & { isTimerOperation: boolean };
            
            // Update the item in the local timesheets array
            if (this.timesheets) {
              const index = this.timesheets.findIndex(t => t.key === updatedItem.key);
              if (index !== -1) {
                console.log('### DEBUG: Updating timer in local timesheets array at index:', index);
                this.timesheets[index] = {...updatedItem};
              } else {
                console.warn('### DEBUG: Could not find timer in local timesheets array with key:', updatedItem.key);
              }
            }
            
            // Show notification if we updated a running timer
            if (isRunningTimer && !isTimerOperation) {
              this.snackBar.open('Running timer details updated', 'Close', {
                duration: 3000,
                horizontalPosition: 'end',
                verticalPosition: 'bottom'
              });
            }
            
            if (emit) {
              console.log('### DEBUG: Emitting actioned event with updatedItem');
              this.actioned.emit(updatedItem);
            }
            console.log('### DEBUG: Resolving promise with updatedItem');
            resolve(updatedItem);
          })
          .catch(error => {
            console.error('### DEBUG: Error in timeSheetService.update:', error);
            reject(error);
          });
      } else {
        console.log('### DEBUG: Creating new timesheet entry');
        this.timeSheetService
          .save(payload, this.userService.currentUser.key)
          .then((g: TimeSheetEntity) => {
            console.log('### DEBUG: timeSheetService.save completed successfully, new key:', g.key);
            this.timeSheetForm.controls['key'].setValue(g.key);
            this.isEdit = true;
            // Add the new item to the timesheets array
            if (this.timesheets) {
              console.log('### DEBUG: Adding new timer to local timesheets array');
              this.timesheets = [g, ...this.timesheets];
            } else {
              console.log('### DEBUG: Creating new timesheets array with new timer');
              this.timesheets = [g];
            }
            if (emit) {
              console.log('### DEBUG: Emitting actioned event with new timer');
              this.actioned.emit({...g, isTimerOperation: isTimerOperation});
            }
            console.log('### DEBUG: Resolving promise with new timer');
            resolve(g);
          })
          .catch(error => {
            console.error('### DEBUG: Error in timeSheetService.save:', error);
            reject(error);
          });
      }
    });
  }

  public resetForm() {

    this.isEdit = false;
    this.timeSheetForm.reset({
      billableItem: '',
      card: '',
      boardKey: '',
      item: '',
      notes: '',
      from: '',
      to: '',
      total: '',
      key: '',
      calendarId: '',
      date: new Date(),
    });
  }

  public startTimer() {
    // Guard against multiple simultaneous calls
    if (this.isStartingTimer) {
      console.log('Timer start operation already in progress, ignoring duplicate call');
      return;
    }
    
    this.isStartingTimer = true;
    
    try {
      // Check if there's already a running timer for this card
      if (this.timesheets) {
        const runningTimer = this.timesheets.find(t => !t.to);
        if (runningTimer) {
          // If there's already a running timer, select it instead of starting a new one
          console.log('Found existing running timer, selecting it instead of starting new one:', runningTimer.key);
          this.itemSelected(runningTimer);
          this.isStartingTimer = false;
          return;
        }
      }
      
      // Continue with normal timer start process
      this.checkForRunningTimersAndStop();
      this.startButtonDelay = true;
      
      // Ensure we have a date value set to today if not already set
      if (!this.timeSheetForm.controls['date'].value) {
        const today = new Date();
        console.log('Setting timesheet date to today:', today);
        this.timeSheetForm.controls['date'].setValue(today);
      }
      
      // Set card info if available - but don't require it
      if (this.card) {
        console.log('Setting card value in timesheet form:', this.card);
        this.timeSheetForm.controls['card'].setValue(this.card);
      }
      
      // Make sure the full notes string is set if available and not already populated
      if (this.notes) {
        // Use our utility method for consistent handling
        this.ensureNotesSet(this.notes);
      }
      
      // Set the current time as the start time
      const startTime = new Date().toLocaleTimeString('en-ZA', {
          hour12: false,
          hour: 'numeric',
          minute: 'numeric',
      });
      console.log('Setting start time for timer:', startTime);
      this.timeSheetForm.controls['from'].setValue(startTime);
      
      // Clear the end time for the new timer
      this.timeSheetForm.controls['to'].setValue('');
      
      // Create in-memory timer object that can be saved later
      // Allow timer to start even if card, billableItem and item aren't set yet
      // Those will be required when stopping the timer
      const tempTimesheet = {
        from: startTime,
        to: '',
        notes: this.timeSheetForm.controls['notes'].value,
        date: new Date().getTime(),
        billableItem: this.timeSheetForm.controls['billableItem'].value,
        item: this.timeSheetForm.controls['item'].value,
        card: this.timeSheetForm.controls['card'].value
      };
      
      // Emit immediately so the card-tabs component knows a timer is running
      console.log('Emitting timer started event with temporary timesheet:', tempTimesheet);
      this.timerStarted.emit(tempTimesheet as TimeSheetEntity);
      
      // Only save to database if form is valid AND we have required fields
      const hasRequiredFields = this.timeSheetForm.controls['billableItem'].value && 
                              this.timeSheetForm.controls['item'].value;
      
      if (hasRequiredFields && this.timeSheetForm.valid && !this.popup) {
        console.log('Timer has required fields, saving to database');
        this.save(true, true).then((g) => {
          console.log('Timer started and saved successfully - timesheet key:', g?.key);
          // Set this as the currently selected timer
          if (g && g.key) {
            this.isEdit = true;
            this.timeSheetForm.controls['key'].setValue(g.key);
          }
          this.isStartingTimer = false;
        }).catch(() => {
          this.isStartingTimer = false;
        });
      } else {
        console.log('Timer started without saving - required fields will be collected when stopping');
        this.isStartingTimer = false;
      }

      setTimeout(() => {
        this.startButtonDelay = false;
      }, 1500);
    } catch (error) {
      console.error('Error in startTimer:', error);
      this.isStartingTimer = false;
    }
  }

  public stopTimer() {
    console.log('### DEBUG: stopTimer() called');
    console.log('### DEBUG: Current form state:', {
      key: this.timeSheetForm.controls['key'].value,
      from: this.timeSheetForm.controls['from'].value,
      to: this.timeSheetForm.controls['to'].value,
      isEdit: this.isEdit,
      valid: this.timeSheetForm.valid
    });

    const endTime = new Date().toLocaleTimeString('en-ZA', {
      hour12: false,
      hour: 'numeric',
      minute: 'numeric',
    });
    
    console.log(`### DEBUG: Setting end time to: ${endTime}`);
    this.timeSheetForm.controls['to'].setValue(endTime);
    
    console.log('### DEBUG: Form validity after setting end time:', this.timeSheetForm.valid);
    console.log('### DEBUG: Form errors:', this.getFormValidationErrors());
    
    if (this.timeSheetForm.valid) {
      console.log('### DEBUG: Form is valid, proceeding to save');
      if (!this.popup) {
        console.log('### DEBUG: Not in popup mode, calling save()');
        this.save(true, true).then((g) => {
          console.log('### DEBUG: Save completed successfully', g);
          this.timerStopped.emit(this.timeSheetForm.value);
        }).catch(error => {
          console.error('### DEBUG: Error stopping timer:', error);
        });
      } else {
        // If in popup mode, save and auto-close the dialog
        console.log('### DEBUG: In popup mode, calling save()');
        this.save(true, true).then((g) => {
          console.log('### DEBUG: Save in popup mode completed');
          this.timerStopped.emit(this.timeSheetForm.value);
          this.dialogRef.close(g); // Auto-close the dialog after stopping the timer
        }).catch(error => {
          console.error('### DEBUG: Error stopping timer in popup mode:', error);
        });
      }
    } else {
      console.error('### DEBUG: Form is invalid, cannot stop timer');
      console.error('### DEBUG: Form value:', this.timeSheetForm.value);
    }
  }

  // Helper method to get form validation errors for debugging
  private getFormValidationErrors(): any {
    const errors: any = {};
    Object.keys(this.timeSheetForm.controls).forEach(key => {
      const control = this.timeSheetForm.get(key);
      if (control && control.errors) {
        errors[key] = control.errors;
      }
    });
    return errors;
  }

  private checkForRunningTimersAndStop() {
    if (this.timesheets) {
      const runningTimers = this.timesheets.filter((x) => !x.to);
      if (runningTimers.length > 0) {
        // Create a counter to track completed operations
        let completedUpdates = 0;
        const totalUpdates = runningTimers.length;
        
        runningTimers.forEach((timer) => {
          timer.to = new Date().toLocaleTimeString('en-ZA', {
            hour12: false,
            hour: 'numeric',
            minute: 'numeric',
          });
          timer.total = this.dateHelper.calculateTimeDifferenceMinutes(
            timer.from,
            timer.to
          );
          this.timeSheetService
            .update(
              timer,
              this.userService.currentUser.key,
              timer.key ? timer.key : ''
            )
            .then((g: any) => {
              // Only emit the actioned event once all updates are complete
              // to prevent cascading calls
              completedUpdates++;
              if (completedUpdates === totalUpdates) {
                // Use a timeout to ensure we don't cause immediate reactions
                setTimeout(() => {
                  this.actioned.emit(g);
                }, 100);
              }
            });
        });
      }
    }
  }

  public addHour() {
    // Add one hour to the 'to' field
    let to = this.timeSheetForm.controls['to'].value;
    let [hours, minutes] = to.split(':');
    let toTime = new Date();
    toTime.setHours(Number(hours));
    toTime.setMinutes(Number(minutes));
    toTime.setHours(toTime.getHours() + 1);

    // Handle the case when 'to' reaches 24:00
    if (toTime.getHours() > 23) {
      toTime.setHours(23); // Set the hour to 23
      toTime.setMinutes(59); // Set the minutes to 59
    }

    this.timeSheetForm.controls['to'].setValue(
      toTime.toLocaleTimeString('en-ZA', {
        hour12: false,
        hour: 'numeric',
        minute: 'numeric',
      })
    );
  }

  public itemSelected(item: TimeSheetEntity): void {
    this.timeSheetForm.patchValue(item);
    this.timeSheetForm.controls['date'].setValue(new Date(item.date));
    
    this.isEdit = true;

    // Trigger billableItemSelected to populate the items dropdown
    if (item.billableItem) {
      this.billableItemSelected({ value: item.billableItem }, false);
    }
    
    // If this is a running timer, provide visual feedback
    if (!item.to) {
      console.log('Selected a running timer:', item.key);
    }

    this.scroll();
  }

  public addItem(emit: boolean = true): void {

    this.isEdit = false;

    // Get values from the last timesheet if available
    let fromTime = '';
    let billableItem = this.timeSheetForm.value.billableItem;
    let card = this.timeSheetForm.value.card;
    let boardKey = this.timeSheetForm.value.boardKey;
    let item = '';
    // Keep the existing notes, which should be the card name
    let notes = this.timeSheetForm.value.notes;

    // For popup mode, we want to load the last selection from localStorage
    if (this.popup) {
      this.loadLastPopupSelection();
      // Keep the notes field populated with the card name if available
      this.timeSheetForm.controls['notes'].setValue(notes);
      return;
    }

    this.timeSheetForm.reset({
      billableItem: billableItem,
      card: card,
      boardKey: boardKey,
      item: item,
      notes: notes,
      from: fromTime,
      to: '',
      total: '',
      key: '',
      calendarId: '',
      date: new Date(),
    });

    // Trigger billableItemSelected to populate the items dropdown
    if (billableItem) {
      this.billableItemSelected({ value: billableItem }, false);
    }
  }

  public delete(item: TimeSheetEntity): Promise<void> {
    return this.timeSheetService.deleteItem(
      this.userService.currentUser.key,
      item.key ? item.key : ''
    ).then(() => {
      if (this.timesheets) {
        this.timesheets = this.timesheets.filter(t => t.key !== item.key);
      }
      if (item.key == this.timeSheetForm.controls['key'].value) {
        this.addItem();
      }
    });
  }

  public deleteItem(): void {
    this.delete(this.timeSheetForm.value).then(() => {
      if (this.popup || this.hideAdd) {
        this.dialogRef.close();
      }
    });
  }
  public getAppearance(item: TimeSheetEntity): MatCardAppearance {
    return item.key === this.timeSheetForm.value.key ? 'raised' : 'outlined';
  }

  private scroll() {
    document.getElementById('top')?.scrollIntoView();
  }

  private multipleCaledarsSelected(): boolean {
    if (!this.calendar) return false;
    return this.calendar.filter((x) => x.checked).length > 1;
  }

  public async requestCalendarAccess() {
    this.showCalendar = true;
    this.calendarService.initiateCalendarAccess();
  }

  public async requestMicrosoftCalendarAccess(): Promise<void> {
    try {
      await this.calendarService.initiateMicrosoftCalendarAccess();
    } catch (error) {
      console.error('Error requesting Microsoft calendar access:', error);
    }
  }

  public toggleCalendar() {
    this.showCalendar = !this.showCalendar;
    if (this.showCalendar) {
      // Recalculate panel width when showing
      setTimeout(() => this.calculatePanelWidth(), 0);
      if (this.hasGoogleAccess || this.hasMicrosoftAccess) {
        this.loadCalendars();
      }
    }
    this.cdr.detectChanges();
  }

  private calculatePanelWidth(): void {
    const screenWidth = window.innerWidth;
    // Ensure we leave enough space for the modal (MODAL_WIDTH) plus some padding
    const maxAllowedWidth = screenWidth - this.MODAL_WIDTH - 100; // 100px safety margin
    // Cap at 400px to prevent too wide panels
    const panelWidth = Math.min(maxAllowedWidth, 200);
    // Ensure minimum width of 300px if screen is large enough
    this.panelWidth = `${Math.max(200, panelWidth)}px`;
  }

  public handleMultipleEventsSelected(events: CalendarEvent[]) {
    this.multipleSelected = events.length > 0;
    if (this.multipleSelected) {
      this.selectedItems = events.map(event => event.id);
      // Clear the current form for multiple selection
      this.timeSheetForm.patchValue({
        from: '',
        to: '',
        notes: '',
        total: ''
      });
    }
  }

  private processExistingItem(item: TimeSheetEntity) {
    
    if (item.key) {
      this.isEdit = true;
    } else {
      this.isEdit = false;
    }
    
    
    this.timeSheetForm.patchValue({
      billableItem: item.billableItem,
      item: item.item,
      notes: item.notes,
      from: item.from,
      to: item.to,
      total: item.total,
      key: item.key,
      date: new Date(item.date),
      card: item.card,
      boardKey: (item as any).boardKey || '',
    });

    // Find and set the billable item to populate the items dropdown
    if (item.billableItem) {
      this.isItemsLoading = true;

      const selectedBillableItem = this.billableItems.find(b => b.key === item.billableItem);


      if (selectedBillableItem) {
        this.billableItem = selectedBillableItem;
        this.items = this.filterItemsByUserRoles(selectedBillableItem.items, this.userService.currentUser);

        // For new entries (no key) with no item pre-set, auto-pick the first role-filtered item
        if (!item.key && !item.item && this.items && this.items.length > 0) {
          this.items.sort((a, b) => a.name.localeCompare(b.name));
          this.timeSheetForm.controls['item'].setValue(this.items[0].name);
        }
      }

      this.isItemsLoading = false;
    }
  }

  private autoSelectProject(): void {
    const user = this.userService.currentUser;
    if (!user?.key || !this.billableItems?.length) return;
    
    // Prevent re-running if we already have a selected item
    if (this.timeSheetForm.controls['billableItem'].value) {
      return;
    }
    
    console.log('Auto-selecting project, available items:', this.billableItems.length);
    
    // Get last selected item from user settings
    this.userSettingsService.get(user.key, 'lastSelectedItem')
      .get()
      .subscribe(doc => {
        const lastItem = doc.exists ? doc.data()?.value : null;
        console.log('Last selected item from user settings:', lastItem);
        
        // Try to use last selected item
        if (lastItem) {
          const lastUsedItem = this.billableItems.find(item => item.key === lastItem);
          console.log('Found matching item for last selected:', lastUsedItem);
          
          if (lastUsedItem) {
            console.log('Setting billableItem to last used:', lastUsedItem.key);
            
            // Set the form value and items without emitting change event
            this.timeSheetForm.controls['billableItem'].setValue(lastUsedItem.key);
            this.billableItem = lastUsedItem;
            this.items = this.filterItemsByUserRoles(lastUsedItem.items, this.userService.currentUser);
            if (this.items) {
              this.items.sort((a, b) => a.name.localeCompare(b.name));
            }
            
            // Make sure the changes are detected
            this.cdr.detectChanges();
            return;
          }
        }
        
        // Fallback: Use default project or the only available item
        if (this.billableItems.length === 1) {
          // If there's only one item, use it
          const singleItem = this.billableItems[0];
          console.log('Only one billable item available, using:', singleItem.key);
          
          // Set the form value and items without emitting change event
          this.timeSheetForm.controls['billableItem'].setValue(singleItem.key);
          this.billableItem = singleItem;
          this.items = this.filterItemsByUserRoles(singleItem.items, this.userService.currentUser);
          if (this.items) {
            this.items.sort((a, b) => a.name.localeCompare(b.name));
          }
          
          // Make sure the changes are detected
          this.cdr.detectChanges();
        } else if (this.boardDefaultProject) {
          // Try to use board default project
          const defaultItem = this.billableItems.find(item => item.key === this.boardDefaultProject);
          console.log('Using board default project:', this.boardDefaultProject, defaultItem);
          
          if (defaultItem) {
            console.log('Setting billableItem to board default:', defaultItem.key);
            
            // Set the form value and items without emitting change event
            this.timeSheetForm.controls['billableItem'].setValue(defaultItem.key);
            this.billableItem = defaultItem;
            this.items = this.filterItemsByUserRoles(defaultItem.items, this.userService.currentUser);
            if (this.items) {
              this.items.sort((a, b) => a.name.localeCompare(b.name));
            }
            
            // Make sure the changes are detected
            this.cdr.detectChanges();
          }
        }
      });
  }

  private updateFilteredClients() {
    this.filteredClients = this.clients.filter((client) => {
      const billableItems = this.billableItems.filter((item) => item.client === client.key);
      return billableItems.length > 0;
    });
  }
}
